import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Modifications as ModificationsType } from '@src/types/graphql-types'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import { splitMessages } from '../../utils'
import useTubeValidation from '../../useTubeValidation'
import useFormikHelper from '../../useFormikHelper'
import { ModificationTypes } from '../../enum'
import { Grid, makeStyles, Theme, Typography } from '@material-ui/core'
import { InsertModificationArgs } from '../../ProductsConfiguration'
import ModificationDropDownBaseBiTube from './ModificationDropDownBaseBiTube'

interface FiveAndInternalModificationsProps {
  disabled: boolean
  fiveModData: ModificationsType
  internalModData: ModificationsType
  insertModificationIntoSequence: ({
    value,
    resetMod,
    isAntiSense,
  }: InsertModificationArgs) => Promise<string>
  isAntiSense?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  titleStyle: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 900,
  },
  modificationDropDownContainer: {
    marginTop: theme.spacing(2),
  },
}))

const FiveAndInternalModifications: React.FC<
  FiveAndInternalModificationsProps
> = ({
  fiveModData,
  internalModData,
  insertModificationIntoSequence,
  isAntiSense,
}) => {
  const classes = useStyles()
  const formik = useFormikHelper()
  const { validationAPI } = useTubeValidation()
  const fiveModType = isAntiSense
    ? 'antisenceFiveMod'
    : ModificationTypes.FiveMod
  const onFiveModChange = (value: string) => {
    formik.setOligoFieldValue(fiveModType, value, false)
    validationAPI({ ...formik.validationFields, [fiveModType]: value })
  }

  const onInternalModChange = (value: string) => {
    formik.setOligoFieldValue('internalMod', value, false)
    insertModificationIntoSequence({
      value,
      isAntiSense: true,
    })
  }

  const fiveModErrorList: string[] =
    (formik.errors?.[fiveModType]
      ? splitMessages(formik.errors[fiveModType] || '')
      : []) || []

  return (
    <>
      <Grid container xs={12} spacing={2}>
        <Grid item xs={12}>
          <Typography className={classes.titleStyle}>
            <FormattedMessage
              id="CONF_FIVE_AND_THREE_MODIFICATIONS_TITLE"
              defaultMessage="*5’ & Internal Modifications"
            />
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormattedMessage
            id="CONF_FIVE_AND_THREE_MODIFICATIONS_MESSAGE"
            defaultMessage="Either or both modifications must be selected. Insert your cursor at the desired position within the sequence field above and click the ‘Add’ link below."
          />
        </Grid>
      </Grid>

      <Grid container xs={12}>
        <Grid item xs={6}>
          <Grid
            container
            xs={12}
            spacing={4}
            className={classes.modificationDropDownContainer}
          >
            <Grid item xs={6}>
              <ModificationDropDownBaseBiTube
                modificationType={ModificationTypes.FiveMod}
                title={
                  <FormattedMessage
                    id="_PC_SPEC_5MOD"
                    defaultMessage="5' Modifications"
                  />
                }
                modifications={fiveModData?.category?.[0].modifications || []}
                onChange={onFiveModChange}
                value={formik.selectedOligoValues[fiveModType]}
              />
            </Grid>
            <Grid item xs={6}>
              <ModificationDropDownBaseBiTube
                modificationType={ModificationTypes.InternalMod}
                title={
                  <FormattedMessage
                    id="INTERNAL_MODIFICATIONS"
                    defaultMessage="Internal Modifications"
                  />
                }
                modifications={
                  internalModData?.category?.[0].modifications || []
                }
                onChange={onInternalModChange}
                value={
                  formik.selectedOligoValues[ModificationTypes.InternalMod]
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {fiveModErrorList.length > 0 && (
        <ValidationErrors errors={fiveModErrorList} configuratorsRedesign />
      )}
    </>
  )
}

export default FiveAndInternalModifications
